import React, { useCallback, useContext, useMemo, useState } from "react";
import { graphql } from "react-relay";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";

import ViewportContext from "~/common/ViewportContext";
import useMedia from "~/lib/hooks/useMedia";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { SLIDEOVER_MAX_WIDTH } from "~/nav";
import {
	CategoryList as NoteCategoryList,
	CategoryToPath as NoteCategoryToPath,
	FixedCategory as NoteFixedCategory,
	PathToCategory as NotePathToCategory,
} from "~/noteTemplate/category";
import {
	CategoryList as FeedbackCategoryList,
	CategoryToPath as FeedbackCategoryToPath,
	FixedCategory as FeedbackFixedCategory,
	PathToCategory as FeedbackPathToCategory,
} from "~/templatesSection/feedback/category";

import type { TemplatesPanelQuery as QueryType } from "~/__generated__/TemplatesPanelQuery.graphql";

import styles from "~/nav/panels/Templates/TemplatesPanel.module.css";

const ViewingSwitcher: React.FC<{ onChangeView?: () => void }> = ({ onChangeView }) => {
	const history = useHistory();
	const match = useRouteMatch<{ viewing: "notes" | "feedback" }>("/templates-section/:viewing");

	const viewNoteTemplates = useCallback(() => {
		onChangeView?.();
		history.push("/templates-section/notes/");
	}, [onChangeView, history]);

	const viewFeedbackTemplates = useCallback(() => {
		onChangeView?.();
		history.push("/templates-section/feedback/");
	}, [onChangeView, history]);

	return (
		<div className={styles.viewingSwitcher}>
			<button onClick={viewNoteTemplates} className={styles.switcherButton}>
				Notes
				{(!match?.params.viewing || match?.params.viewing === "notes") && <div className={styles.activeButton} />}
			</button>
			<button onClick={viewFeedbackTemplates} className={styles.switcherButton}>
				Feedback
				{match?.params.viewing === "feedback" && <div className={styles.activeButton} />}
			</button>
		</div>
	);
};

export type TemplatesPanelProps = {
	collapse: () => void;
};

const PanelContent = ({
	collapse,
	search,
	setSearch,
}: {
	collapse: () => void;
	search: string;
	setSearch: React.Dispatch<string>;
}) => {
	const location = useLocation();
	const history = useHistory();
	const viewingMatch = useRouteMatch<{ viewing: "notes" | "feedback" }>("/templates-section/:viewing");
	const categoryMatch = useRouteMatch<{ category: string }>("/templates-section/:viewing/category/:category");
	const idMatch = useRouteMatch<{ id: string }>("/templates-section/:viewing/:id");
	const { isMobile } = useContext(ViewportContext);

	const catMatch = categoryMatch?.params.category;
	const selectedNoteCategory = useMemo(() => {
		if (catMatch && catMatch in NotePathToCategory) return NotePathToCategory[catMatch];
		return catMatch ? catMatch : idMatch?.params.id ? "" : NoteFixedCategory.MY;
	}, [catMatch, idMatch]);
	const selectedFeedbackCategory = useMemo(() => {
		return catMatch && catMatch in FeedbackPathToCategory ? FeedbackPathToCategory[catMatch] : FeedbackFixedCategory.MY;
	}, [catMatch]);

	const props = useLazyLoadQuery<QueryType>(
		graphql`
			query TemplatesPanelQuery {
				viewer {
					noteTemplates(first: null) {
						edges {
							node {
								tags
							}
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const panelAboveContent = useMedia(`(max-width: ${SLIDEOVER_MAX_WIDTH}px)`);
	const goToTemplateUrl = React.useCallback(
		(searchString: string) => {
			history.replace(`${location.pathname}${searchString.length > 0 ? `?s=${searchString}` : ""}`);
		},
		[location, history],
	);

	const clearSearch = React.useCallback(() => {
		setSearch("");
		if (panelAboveContent) {
			return;
		}
		goToTemplateUrl("");
	}, [setSearch, panelAboveContent, goToTemplateUrl]);

	const setSearchFromEvent = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearch(e.target.value);
			if (panelAboveContent) {
				return;
			}
			goToTemplateUrl(e.target.value);
		},
		[setSearch, panelAboveContent, goToTemplateUrl],
	);

	const searchKeyDownEvent = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (!panelAboveContent || e.key != "Enter") {
				return;
			}
			goToTemplateUrl(search);
		},
		[goToTemplateUrl, panelAboveContent, search],
	);

	// Feedback template section
	const onFeedbackCategoryChange = React.useCallback(
		(category: string) => {
			if (isMobile) collapse();

			const pathCategory = FeedbackCategoryToPath[category];
			history.push(`/templates-section/feedback/category/${pathCategory}/`);
		},
		[collapse, history, isMobile],
	);

	const onNoteCategoryChange = React.useCallback(
		(category: string) => {
			let suffix: string = search.length > 0 ? `?s=${search}` : "";
			if (isMobile) collapse();

			let pathCategory = category;
			if (category in NoteCategoryToPath) {
				pathCategory = NoteCategoryToPath[category];
			}
			history.push(`/templates-section/notes/category/${pathCategory}/${suffix}`);
		},
		[collapse, history, isMobile, search],
	);

	// Feedback template section
	if (viewingMatch?.params.viewing === "feedback") {
		return (
			<FeedbackCategoryList selectedCategory={selectedFeedbackCategory} onSelectionChange={onFeedbackCategoryChange} />
		);
	}

	// Notes template section
	const allTags: Set<string> = new Set<string>();
	props.viewer.noteTemplates.edges.forEach(edge => edge.node.tags.forEach((tag: string) => allTags.add(tag)));
	const tags = Array.from(allTags.values());

	return (
		<NoteCategoryList
			selectedCategory={selectedNoteCategory}
			onSelectionChange={onNoteCategoryChange}
			tagCategories={tags}
			search={search}
			onSearchChange={setSearchFromEvent}
			onSearchClear={clearSearch}
			onSearchKeyDown={searchKeyDownEvent}
		/>
	);
};

export default function TemplatesPanel({ collapse }: TemplatesPanelProps) {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const [search, setSearch] = useState<string>(params.get("s") ?? "");
	const resetSearchBox = useCallback(() => setSearch(""), [setSearch]);

	return (
		<section className={styles.panel}>
			<header className={styles.panelHeader}>
				<h1>Templates</h1>
			</header>
			<ViewingSwitcher onChangeView={resetSearchBox} />
			<main>
				<PanelContent search={search} setSearch={setSearch} collapse={collapse} />
			</main>
		</section>
	);
}
